import { useEffect, useRef, useState } from "react";
import { FACTIONS, type FactionId } from "@/data/factions.ts";
import { MAPS } from "@/data/maps.ts";
import { ROLES, type RoleId } from "@/data/roles.ts";
import { MapView } from "@/features/map/MapView.tsx";
import type { OrderMarker } from "@/features/map/OrderLayer.tsx";
import type { Marker, Route } from "@/session/schemas.ts";
import {
  formatRoomCode,
  generateRoomCode,
  isValidRoomCode,
  normalizeRoomCode,
  ROOM_CODE_LENGTH,
} from "@/session/roomCode.ts";
import { normalizeCallsign, suggestCallsign } from "@/session/identity.ts";
import { Footer } from "./Footer.tsx";

interface JoinScreenProps {
  readonly initialCallsign: string;
  readonly initialFaction: FactionId;
  readonly initialRole: RoleId;
  readonly onStart: (details: {
    code: string;
    callsign: string;
    faction: FactionId;
    role: RoleId;
  }) => void;
}

/**
 * Fake tactical data for the landing page, using real game coordinates so
 * the MapView renders it identically to a live session. Each map gets its
 * own scenario — positions are inside the playable area of that map.
 */
function buildDemoData(mapId: string): {
  orders: OrderMarker[];
  markers: Marker[];
  routes: Route[];
} {
  const now = Date.now();
  const scenarios: Record<
    string,
    {
      orders: OrderMarker[];
      markers: Marker[];
      routes: Route[];
    }
  > = {
    bakurani: {
      orders: [
        {
          id: "d1",
          squadId: "alpha",
          squadTag: "A",
          kind: "attack",
          place: "I9-7",
          at: { x: 90, y: 96 },
        },
        {
          id: "d2",
          squadId: "bravo",
          squadTag: "B",
          kind: "defend",
          place: "H8-1",
          at: { x: 75, y: 108 },
        },
        {
          id: "d3",
          squadId: "charlie",
          squadTag: "C",
          kind: "recon",
          place: "K10-3",
          at: { x: 105, y: 100 },
        },
        {
          id: "d4",
          squadId: "delta",
          squadTag: "D",
          kind: "hold",
          place: "G11-5",
          at: { x: 68, y: 115 },
        },
      ],
      markers: [
        {
          id: "m1",
          kind: "artillery",
          at: { x: 65, y: 120 },
          label: "",
          createdBy: "demo",
          createdAt: now,
          threat: false,
          mapId: "bakurani",
          weaponId: "l81-mortar",
        },
        {
          id: "m2",
          kind: "artillery",
          at: { x: 100, y: 85 },
          label: "",
          createdBy: "demo",
          createdAt: now,
          threat: true,
          mapId: "bakurani",
          weaponId: "sph-2",
        },
        {
          id: "m3",
          kind: "spotted",
          at: { x: 95, y: 92 },
          label: "",
          createdBy: "demo",
          createdAt: now,
          threat: true,
          mapId: "bakurani",
        },
        {
          id: "m4",
          kind: "spotted",
          at: { x: 98, y: 88 },
          label: "",
          createdBy: "demo",
          createdAt: now,
          threat: true,
          mapId: "bakurani",
        },
        {
          id: "m5",
          kind: "fob",
          at: { x: 70, y: 125 },
          label: "",
          createdBy: "demo",
          createdAt: now,
          threat: false,
          mapId: "bakurani",
        },
      ],
      routes: [
        {
          id: "r1",
          label: "",
          waypoints: [
            { x: 60, y: 130 },
            { x: 70, y: 115 },
            { x: 80, y: 105 },
            { x: 90, y: 96 },
          ],
          createdBy: "demo",
          createdAt: now,
          movement: "flight",
          mapId: "bakurani",
        },
        {
          id: "r2",
          label: "",
          waypoints: [
            { x: 120, y: 130 },
            { x: 110, y: 110 },
            { x: 105, y: 100 },
          ],
          createdBy: "demo",
          createdAt: now,
          movement: "flight",
          mapId: "bakurani",
        },
      ],
    },
    ozeti: {
      orders: [
        {
          id: "d1",
          squadId: "alpha",
          squadTag: "A",
          kind: "attack",
          place: "H9-4",
          at: { x: 82, y: 95 },
        },
        {
          id: "d2",
          squadId: "bravo",
          squadTag: "B",
          kind: "move",
          place: "J7-8",
          at: { x: 100, y: 78 },
        },
        {
          id: "d3",
          squadId: "charlie",
          squadTag: "C",
          kind: "defend",
          place: "F10-2",
          at: { x: 58, y: 102 },
        },
      ],
      markers: [
        {
          id: "m1",
          kind: "artillery",
          at: { x: 80, y: 110 },
          label: "",
          createdBy: "demo",
          createdAt: now,
          threat: false,
          mapId: "ozeti",
          weaponId: "sph-2",
        },
        {
          id: "m2",
          kind: "warning",
          at: { x: 90, y: 85 },
          label: "",
          createdBy: "demo",
          createdAt: now,
          threat: true,
          mapId: "ozeti",
        },
        {
          id: "m3",
          kind: "heli",
          at: { x: 65, y: 90 },
          label: "",
          createdBy: "demo",
          createdAt: now,
          threat: false,
          mapId: "ozeti",
        },
      ],
      routes: [
        {
          id: "r1",
          label: "",
          waypoints: [
            { x: 110, y: 60 },
            { x: 95, y: 75 },
            { x: 82, y: 95 },
          ],
          createdBy: "demo",
          createdAt: now,
          movement: "flight",
          mapId: "ozeti",
        },
      ],
    },
    zestafona: {
      orders: [
        {
          id: "d1",
          squadId: "alpha",
          squadTag: "A",
          kind: "defend",
          place: "H9-1",
          at: { x: 78, y: 92 },
        },
        {
          id: "d2",
          squadId: "bravo",
          squadTag: "B",
          kind: "defend",
          place: "I9-5",
          at: { x: 88, y: 95 },
        },
        {
          id: "d3",
          squadId: "charlie",
          squadTag: "C",
          kind: "defend",
          place: "J9-7",
          at: { x: 98, y: 97 },
        },
        {
          id: "d4",
          squadId: "delta",
          squadTag: "D",
          kind: "resupply",
          place: "G12-4",
          at: { x: 68, y: 122 },
        },
      ],
      markers: [
        {
          id: "m1",
          kind: "artillery",
          at: { x: 75, y: 118 },
          label: "",
          createdBy: "demo",
          createdAt: now,
          threat: false,
          mapId: "zestafona",
          weaponId: "l81-mortar",
        },
        {
          id: "m2",
          kind: "artillery",
          at: { x: 105, y: 75 },
          label: "",
          createdBy: "demo",
          createdAt: now,
          threat: true,
          mapId: "zestafona",
          weaponId: "sph-2",
        },
        {
          id: "m3",
          kind: "spotted",
          at: { x: 108, y: 70 },
          label: "",
          createdBy: "demo",
          createdAt: now,
          threat: true,
          mapId: "zestafona",
        },
        {
          id: "m4",
          kind: "fob",
          at: { x: 65, y: 125 },
          label: "",
          createdBy: "demo",
          createdAt: now,
          threat: false,
          mapId: "zestafona",
        },
      ],
      routes: [
        {
          id: "r1",
          label: "",
          waypoints: [
            { x: 120, y: 135 },
            { x: 95, y: 115 },
            { x: 75, y: 100 },
            { x: 68, y: 122 },
          ],
          createdBy: "demo",
          createdAt: now,
          movement: "flight",
          mapId: "zestafona",
        },
      ],
    },
  };

  return scenarios[mapId] ?? scenarios["bakurani"]!;
}

/**
 * Getting into a session.
 *
 * This screen is used in the sixty seconds between spawning and being needed,
 * usually while someone reads a code out over voice. So: the code field is
 * focused immediately, it accepts a pasted link as happily as six typed
 * characters, and a callsign is pre-filled so nobody has to invent one.
 *
 * Hosting and joining are the same act — there is no "create" step, because
 * a session is just a code that somebody reached first.
 */
export function JoinScreen({
  initialCallsign,
  initialFaction,
  initialRole,
  onStart,
}: JoinScreenProps): React.ReactElement {
  const [code, setCode] = useState("");
  const [callsign, setCallsign] = useState(initialCallsign || suggestCallsign());
  const [faction, setFaction] = useState<FactionId>(initialFaction);
  const [role, setRole] = useState<RoleId>(initialRole);

  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    codeInputRef.current?.focus();
  }, []);

  // A code in the URL means somebody shared a link: fill it in and let them go.
  useEffect(() => {
    const fromPath = normalizeRoomCode(window.location.pathname);
    if (isValidRoomCode(fromPath)) setCode(fromPath);
  }, []);

  const canStart = isValidRoomCode(code);

  const submit = (): void => {
    if (!canStart) return;
    onStart({ code, callsign: normalizeCallsign(callsign), faction, role });
  };

  // Pick a random map with imagery each load so the landing always looks fresh.
  const [demoMap] = useState(() => {
    const withImagery = MAPS.filter((entry) => entry.hasImagery);
    return withImagery[Math.floor(Math.random() * withImagery.length)] ?? withImagery[0]!;
  });

  // Fake tactical data rendered through the real MapView pipeline — same
  // tiles, same grid, same icons, so the landing looks exactly like what
  // you get once you enter a session.
  const [demoData] = useState(() => buildDemoData(demoMap.id));

  return (
    <div className="relative flex h-full flex-col">
      {/* The real map, rendered exactly like in a session, just dimmed. */}
      <div className="pointer-events-none absolute inset-0 opacity-40">
        <MapView
          map={demoMap}
          showGrid={true}
          showLabels={true}
          orders={demoData.orders}
          markers={demoData.markers}
          routes={demoData.routes}
          myId=""
          canManageAllMarkers={false}
          onRemoveMarker={() => {}}
          onRemoveRoute={() => {}}
        />
      </div>

      <div className="relative z-10 flex min-h-0 flex-1 items-center justify-center px-4">
        <div className="w-full max-w-md rounded-lg border border-border-subtle bg-surface/90 p-6 shadow-2xl backdrop-blur-sm">
          <h1 className="mb-1 text-lg font-bold tracking-wide text-text-primary">
            WARDOGS COMMAND
          </h1>
          <p className="mb-6 text-sm text-text-muted">
            Join your team&apos;s session, or start one by inventing a code.
          </p>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
            className="space-y-5"
          >
            <Field label="Session code" hint="Six characters. Paste a share link too.">
              <div className="flex gap-2">
                <input
                  ref={codeInputRef}
                  value={formatRoomCode(code)}
                  onChange={(event) => setCode(normalizeRoomCode(event.target.value))}
                  placeholder="KX7 M2P"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  aria-label="Session code"
                  className="numeric touch-target w-full rounded border border-border-subtle bg-surface-sunken px-3 text-lg tracking-[0.25em] text-text-primary uppercase placeholder:text-text-muted focus:border-accent focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setCode(generateRoomCode())}
                  title="Generate a new code"
                  className="touch-target shrink-0 rounded border border-border-subtle px-3 text-xs text-text-secondary transition-colors hover:border-border-strong hover:text-text-primary"
                >
                  New
                </button>
              </div>
              <CodeProgress length={code.length} />
            </Field>

            <Field label="Callsign" hint="What your team will see and say.">
              <input
                value={callsign}
                onChange={(event) => setCallsign(event.target.value)}
                maxLength={20}
                aria-label="Callsign"
                className="touch-target w-full rounded border border-border-subtle bg-surface-sunken px-3 text-text-primary focus:border-accent focus:outline-none"
              />
            </Field>

            <Field label="Faction">
              <div className="grid grid-cols-3 gap-2">
                {FACTIONS.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => setFaction(entry.id)}
                    title={entry.blurb}
                    className="touch-target rounded border px-2 text-xs font-semibold transition-all"
                    style={
                      entry.id === faction
                        ? {
                            backgroundColor: entry.color,
                            color: entry.onColor,
                            borderColor: entry.color,
                          }
                        : {
                            color: entry.color,
                            borderColor: "var(--color-border-subtle)",
                            opacity: 0.6,
                          }
                    }
                  >
                    {entry.label}
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Role" hint="Change it any time. It only says what you are doing now.">
              <select
                value={role}
                onChange={(event) => setRole(event.target.value as RoleId)}
                aria-label="Role"
                className="touch-target w-full rounded border border-border-subtle bg-surface-sunken px-3 text-text-primary focus:border-accent focus:outline-none"
              >
                {ROLES.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.label} — {entry.blurb}
                  </option>
                ))}
              </select>
            </Field>

            <button
              type="submit"
              disabled={!canStart}
              className="touch-target w-full rounded bg-accent px-4 font-semibold text-surface transition-opacity disabled:cursor-not-allowed disabled:opacity-30"
            >
              {canStart ? `Enter ${formatRoomCode(code)}` : "Enter a session code"}
            </button>
          </form>

          <p className="mt-6 text-[11px] leading-relaxed text-text-muted">
            No account, no sign-up.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}

/** Six dots that fill as the code is typed. Progress without a wall of text. */
function CodeProgress({ length }: { readonly length: number }): React.ReactElement {
  return (
    <div className="mt-2 flex gap-1" aria-hidden>
      {Array.from({ length: ROOM_CODE_LENGTH }, (_, index) => (
        <span
          key={index}
          className="h-0.5 flex-1 rounded-full transition-colors"
          style={{
            backgroundColor:
              index < length ? "var(--color-accent)" : "var(--color-border-subtle)",
          }}
        />
      ))}
    </div>
  );
}

/**
 * A labelled field.
 *
 * `<fieldset>`/`<legend>`, not `<label>` — some of these wrap a single input
 * (which a `<label>` can associate with), but Faction wraps three buttons,
 * and a `<label>` around a button group has no single control to associate
 * with. Browsers then fall back to naming the first button from the whole
 * wrapper's text, so it announces as "FACTION Valkyra Manticore" instead of
 * "Lonestar" — wrong for every field with more than one control in it.
 * `<fieldset>` is built for exactly this and costs nothing visually once its
 * default chrome is reset.
 */
function Field({
  label,
  hint,
  children,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <fieldset className="m-0 block border-0 p-0">
      <legend className="mb-1.5 block text-xs font-medium tracking-wide text-text-secondary uppercase">
        {label}
      </legend>
      {children}
      {hint && <span className="mt-1.5 block text-[11px] text-text-muted">{hint}</span>}
    </fieldset>
  );
}
